import axios from "axios";
import {
  isTenancyFrontendEnabled,
  isTenantHost,
  isAdminHost,
  resolveApiBaseUrl,
  adminDomain,
} from "@/utils/hostname";

/** Paths that must stay on the central API even on tenant workspaces. */
const CENTRAL_ONLY_API_PREFIXES = [
  "public/register-company",
  "demos/request",
  "central/tenant-lookup",
];

function centralApiOrigins() {
  const configured =
    process.env.VUE_APP_API_URL || "http://127.0.0.1:8000/api/";
  const origins = new Set();

  try {
    const url = new URL(configured);
    const port = url.port ? `:${url.port}` : "";
    origins.add(`${url.protocol}//${url.hostname}${port}`);
    if (url.hostname === "127.0.0.1") {
      origins.add(`${url.protocol}//localhost${port}`);
    } else if (url.hostname === "localhost") {
      origins.add(`${url.protocol}//127.0.0.1${port}`);
    }
  } catch {
    origins.add("http://127.0.0.1:8000");
    origins.add("http://localhost:8000");
  }

  return [...origins];
}

function normalizeApiPath(pathname) {
  return pathname.replace(/^\/+/, "").replace(/^api\/?/, "");
}

function isCentralOnlyApiPath(pathname) {
  const normalized = normalizeApiPath(pathname);
  return CENTRAL_ONLY_API_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
  );
}

function adminApiOrigin() {
  try {
    const configured =
      process.env.VUE_APP_API_URL || "http://127.0.0.1:8000/api/";
    const url = new URL(configured);
    const port = url.port ? `:${url.port}` : "";
    return `${url.protocol}//${adminDomain()}${port}`;
  } catch {
    return `http://${adminDomain()}:8000`;
  }
}

function rewriteRequestUrl(url) {
  if (!url || typeof url !== "string") return url;

  if (isTenancyFrontendEnabled() && isAdminHost()) {
    const adminOrigin = adminApiOrigin();
    for (const origin of centralApiOrigins()) {
      const centralPrefix = `${origin}/api`;
      if (url.startsWith(centralPrefix)) {
        const pathAndQuery = url.slice(centralPrefix.length);
        return `${adminOrigin}/api${pathAndQuery.startsWith("/") ? "" : "/"}${pathAndQuery}`;
      }
    }
  }

  if (!isTenancyFrontendEnabled() || !isTenantHost()) return url;

  const tenantBase = resolveApiBaseUrl().replace(/\/+$/, "");

  for (const origin of centralApiOrigins()) {
    const centralPrefix = `${origin}/api`;
    if (!url.startsWith(centralPrefix)) continue;

    const pathAndQuery = url.slice(centralPrefix.length);
    const pathname = pathAndQuery.split("?")[0];
    if (isCentralOnlyApiPath(pathname)) {
      return url;
    }

    return `${tenantBase}${pathAndQuery.startsWith("/") ? "" : "/"}${pathAndQuery}`;
  }

  return url;
}

function stripTrailingSlash(url) {
  if (!url || typeof url !== "string") return url;
  const hashIdx = url.indexOf("#");
  const hash = hashIdx >= 0 ? url.slice(hashIdx) : "";
  const withoutHash = hashIdx >= 0 ? url.slice(0, hashIdx) : url;
  const qIdx = withoutHash.indexOf("?");
  const query = qIdx >= 0 ? withoutHash.slice(qIdx) : "";
  const path = qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash;
  // LiteSpeed 301 /path/ → /path turns POST into GET and breaks creates.
  return path.replace(/\/+$/, "") + query + hash;
}

let configured = false;

/** Rewrite hardcoded central API URLs when the SPA runs on a tenant subdomain. */
export function configureAxiosTenancy() {
  if (configured) return;
  configured = true;

  axios.interceptors.request.use((config) => {
    if (config.url) {
      config.url = stripTrailingSlash(rewriteRequestUrl(config.url));
    }

    if (
      isTenancyFrontendEnabled() &&
      (isTenantHost() || isAdminHost()) &&
      config.url &&
      !/^https?:\/\//i.test(config.url)
    ) {
      config.baseURL = resolveApiBaseUrl();
    }

    return config;
  });
}
